import {
    type BTFileSystem,
    type FileViewOptions,
} from "legacyComponents/FileUploadContainer.types";
import moment, { type MomentInput } from "moment";

import { type BTLoginTypes, FolderTypes, ImageResizeMode } from "types/enum";
import { type BTViewingPermissions } from "types/filePermissions";

import { dateSorter } from "utilities/sort/sort";

import { type IBTColumnProps } from "commonComponents/btWrappers/BTTable/BTTable";
import {
    showClientViewingPermissions,
    showSubViewingPermissions,
} from "commonComponents/entity/media/common/MediaContextMenu/MediaContextMenu.utility";
import { RenameCell } from "commonComponents/entity/media/common/MediaTable/RenameCell";
import MediaViewingPermissionReadonly from "commonComponents/entity/media/MediaViewingPermissionReadonly/MediaViewingPermissionReadonly";
import { DateDisplayDate } from "commonComponents/utilities/DateDisplay/DateDisplayDate";
import { IconFile } from "commonComponents/utilities/IconFile/IconFile";
import TextTruncate from "commonComponents/utilities/TextTruncate/TextTruncate";

import { getLightboxThumbnailUrl } from "entity/media/common/media.utils";

export function thumbnailColumn({
    canClickFile,
    width,
    onClick,
}: {
    width?: string | number;
    onClick: (e: React.MouseEvent<HTMLImageElement, MouseEvent>, file: BTFileSystem) => void;
    canClickFile?: (file: BTFileSystem) => boolean;
}): IBTColumnProps<BTFileSystem> {
    return {
        width,
        dataIndex: "extension",
        title: "",
        render: (extension: string, file) => {
            const isFolder = !!file.folderType && file.folderType !== FolderTypes.NotAFolder;
            const isGlobalFolder =
                !!file.folderType && file.folderType === FolderTypes.GlobalFolder;
            const canClick = !!onClick && canClickFile ? canClickFile?.(file) : true;
            const handleClick = canClick
                ? (e: React.MouseEvent<HTMLImageElement, MouseEvent>) => onClick(e, file)
                : undefined;
            return (
                <div
                    className="flex justify-content-center"
                    data-testid={`thumbnail-column-${file.id}`}
                >
                    <IconFile
                        isFolder={isFolder}
                        isGlobalFolder={isGlobalFolder}
                        fileType={extension}
                        size="small"
                        thumbnail={getLightboxThumbnailUrl(file)}
                        thumbnailSize="medium"
                        thumbnailResizeMode={ImageResizeMode.Max}
                        onClick={handleClick}
                    />
                </div>
            );
        },
    };
}

export function titleColumn({
    canClickFile,
    width,
    onClick,
    renamingId,
    isSavingRename,
    onSubmitRename,
    onCancelRename,
}: {
    width?: string | number;
    onClick?: (file: BTFileSystem) => void;
    canClickFile?: (file: BTFileSystem) => boolean;
    renamingId?: number | null;
    isSavingRename?: boolean;
    onSubmitRename?: (file: BTFileSystem, newTitle: string) => Promise<void>;
    onCancelRename?: () => void;
} = {}): IBTColumnProps<BTFileSystem> {
    return {
        width,
        dataIndex: "title",
        title: "Name",
        render: (title: string, file) => {
            if (renamingId === file.id && onSubmitRename && onCancelRename) {
                return (
                    <RenameCell
                        fileId={file.id}
                        initialTitle={title}
                        isSaving={!!isSavingRename}
                        onSubmit={(newTitle) => onSubmitRename(file, newTitle)}
                        onCancel={onCancelRename}
                    />
                );
            }
            const canClick = !!onClick && canClickFile ? canClickFile?.(file) : true;
            return (
                <TextTruncate style={{ cursor: canClick ? "pointer" : "default" }}>
                    {title}
                </TextTruncate>
            );
        },
        sorter: (a, b) => a.title.localeCompare(b.title),
        onCell: (row) => {
            return {
                onClick: () => {
                    if (renamingId === row.id) return;
                    onClick?.(row);
                },
            };
        },
    };
}

export function permissionsColumn({
    width,
    fileViewOptions,
    loginType,
}: {
    width?: string | number;
    fileViewOptions?: FileViewOptions;
    loginType?: BTLoginTypes;
} = {}): IBTColumnProps<BTFileSystem> {
    return {
        width,
        dataIndex: "viewingPermissions",
        title: "Viewable By",
        align: "left",
        render: (viewingPermissions: BTViewingPermissions | undefined, file) => {
            const showSubs =
                !!viewingPermissions?.showSubs &&
                showSubViewingPermissions(file, false, fileViewOptions, loginType);
            const showClients =
                !!viewingPermissions?.showOwner &&
                showClientViewingPermissions(file, false, fileViewOptions, loginType);
            return (
                <div className="viewable-by-cell">
                    <MediaViewingPermissionReadonly
                        entityId={file.id}
                        entityType="file"
                        showSubs={showSubs}
                        showAllSubs={viewingPermissions?.showAllSubs}
                        showClient={showClients}
                        justify="start"
                        canSetPermissions={false}
                        showCanAdd={false}
                    />
                </div>
            );
        },
    };
}

export function fileSizeColumn({
    width,
}: {
    width?: string | number;
} = {}): IBTColumnProps<BTFileSystem> {
    return {
        width,
        dataIndex: "fileSizeFormatted",
        title: "Size",
        sorter: (a, b) => a.fileSizeFormatted.localeCompare(b.fileSizeFormatted),
    };
}

export function lastModifiedColumn({
    width,
}: {
    width?: string | number;
} = {}): IBTColumnProps<BTFileSystem> {
    return {
        width,
        dataIndex: "lastModifiedOn",
        title: "Modified On",
        render: (lastModifiedOn: MomentInput | null, file) => {
            if (!lastModifiedOn) {
                return null;
            }
            const dateValue = moment.isMoment(lastModifiedOn)
                ? lastModifiedOn.toDate()
                : new Date(lastModifiedOn as string | number | Date);
            return (
                <TextTruncate className="DateColumn">
                    <div>
                        <DateDisplayDate
                            value={dateValue}
                            showTime
                            data-testid={`last-modified-${file.id}`}
                        />
                    </div>
                </TextTruncate>
            );
        },
        sorter: (a, b) => dateSorter(a.lastModifiedOn, b.lastModifiedOn),
    };
}

export function addedByColumn({
    width,
}: {
    width?: string | number;
} = {}): IBTColumnProps<BTFileSystem> {
    return {
        width,
        dataIndex: "addedBy",
        title: "Added By",
        render: (addedBy) => <TextTruncate className="AddedByColumn">{addedBy}</TextTruncate>,
        sorter: (a, b) => a.addedBy.localeCompare(b.addedBy),
    };
}
